import React from 'react';
import { motion } from 'framer-motion';
import { Target, Layers, TrendingUp, Users, Compass, ArrowRight } from 'lucide-react';
export function AboutPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-34">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-35">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-36">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-37">
            <span className="w-8 h-px bg-teal" data-id="element-38"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-39">
              Company
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-40"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-41">
            About SyNeural
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto mb-6" data-id="element-42">
            SyNeural is the parent platform behind the Civic Judgment Index
            (CJI) and the Neurocognitive Civic Judgment Index (NCJI) — a broader
            framework for human capability, civic development, institutional
            readiness, and leadership readiness.
          </p>

          <p className="text-lg text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-43">
            The company was built around a simple premise: modern societies
            demand enormous amounts of judgment from individuals, institutions,
            and leaders, yet very few systems are designed to deliberately
            strengthen that judgment over time.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-44">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-45">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-46">
            SyNeural exists to help address that gap. Through its platform
            architecture, the company is focused on building a more structured
            way to understand how people think, how they develop, how
            institutions can improve readiness, and how societies can create
            stronger pathways from potential to responsibility. Its work sits at
            the intersection of cognitive assessment, decision-quality
            analytics, opportunity guidance, civic resilience, and leadership
            qualification.
          </p>
        </motion.div>
      </section>

      {/* Our Mission */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-47">
        <div className="max-w-4xl mx-auto" data-id="element-48">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-49">
            <div className="flex items-center gap-4 mb-6" data-id="element-50">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-51">
                <Target size={24} data-id="element-52" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-53">
                Our Mission
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-54">
              SyNeural's mission is to strengthen human capability, improve
              civic judgment, expand opportunity, and support more resilient
              institutions by building systems that help people think more
              clearly, develop more effectively, and lead more responsibly. The
              company is focused not only on assessment, but on progression —
              turning insight into development, readiness, and long-term social
              value.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What We Build */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-55">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-56">
          <div className="flex items-center gap-4 mb-6" data-id="element-57">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-58">
              <Layers size={24} data-id="element-59" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-60">
              What We Build
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-8" data-id="element-61">
            At the center of the SyNeural ecosystem is CJI, the platform's
            analytical foundation for reasoning quality, decision competence,
            manipulation resistance, and leadership diagnostics. Built on that
            foundation is NCJI, a broader developmental and institutional
            architecture that expands into opportunity guidance, lifecycle
            development, public-sector readiness, and executive-level leadership
            qualification.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-62">
            Together, these systems form a unified platform for measuring,
            developing, and scaling judgment across the human and institutional
            spectrum.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-63">
        <div className="max-w-4xl mx-auto" data-id="element-64">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-65">
            <div className="flex items-center gap-4 mb-6" data-id="element-66">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-67">
                <TrendingUp size={24} data-id="element-68" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-69">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-70">
              A society can have wealth, infrastructure, technology, and formal
              institutions, yet still weaken if too many decisions are shaped by
              poor reasoning, emotional reactivity, weak foresight, manipulation
              vulnerability, or degraded leadership. SyNeural was created from
              the belief that stronger institutions require a stronger human
              layer beneath them. That means better reasoning, better support
              structures, stronger readiness pathways, and more competent
              leadership over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-71">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-72">
          <div className="flex items-center gap-4 mb-6" data-id="element-73">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-74">
              <Compass size={24} data-id="element-75" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-76">
              Our Vision
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-77">
            SyNeural's long-term vision is to help create a new global
            infrastructure for human capability and civic development. The
            company sees a future in which societies are better equipped to
            cultivate judgment, strengthen institutions, align talent with
            opportunity, and prepare individuals for higher levels of
            responsibility across education, workforce systems, civil society,
            government, and leadership environments.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-78">
            The goal is not simply to measure readiness, but to help build it.
          </p>
        </motion.div>
      </section>

      {/* Leadership */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-79">
        <div className="max-w-5xl mx-auto" data-id="element-80">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-81">
            <div className="flex items-center gap-4 mb-8" data-id="element-82">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-83">
                <Users size={24} data-id="element-84" />
              </div>
              <h2 className="text-3xl font-heading font-bold" data-id="element-85">Leadership</h2>
            </div>

            <div className="bg-white/5 p-8 lg:p-10 rounded-xl border border-white/10" data-id="element-86">
              <h3 className="text-2xl font-heading font-bold mb-2" data-id="element-87">
                Shawn Blake, CEO
              </h3>
              <p className="text-lg leading-relaxed opacity-90" data-id="element-88">
                Shawn Blake leads SyNeural with a vision centered on human
                capability, civic resilience, and institutional strength. As
                CEO, he is focused on building SyNeural into a platform that can
                help individuals grow, institutions modernize, and leadership
                become more rigorous, more developmental, and more aligned with
                long-term societal stability. Under his leadership, SyNeural is
                positioned not just as a product company, but as the foundation
                for a broader architecture of human development and
                decision-quality at scale.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The SyNeural Approach */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-89">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-90">
          <div className="flex items-center gap-4 mb-8" data-id="element-91">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-92">
              <ArrowRight size={24} data-id="element-93" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-94">
              The SyNeural Approach
            </h2>
          </div>

          <p className="text-lg text-ink-muted leading-relaxed mb-10" data-id="element-95">
            SyNeural is built around a simple operating logic: assess, explain,
            recommend, support, reassess, and advance. That principle runs
            throughout the platform and reflects the company's broader
            philosophy.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-96">
            <div className="bg-surface-muted p-6 rounded-lg border border-line" data-id="element-97">
              <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-98">
                Insight should lead to growth
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-99">
                Understanding capability gaps is only valuable when paired with
                development pathways
              </p>
            </div>

            <div className="bg-surface-muted p-6 rounded-lg border border-line" data-id="element-100">
              <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-101">
                Gaps should be paired with pathways
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-102">
                Every identified weakness should connect to concrete support and
                progression routes
              </p>
            </div>

            <div className="bg-surface-muted p-6 rounded-lg border border-line" data-id="element-103">
              <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-104">
                Outcomes need stronger systems
              </h3>
              <p className="text-sm text-ink-muted" data-id="element-105">
                Better results require better institutional and developmental
                infrastructure
              </p>
            </div>
          </div>

          <p className="text-lg text-ink-muted leading-relaxed mt-10" data-id="element-106">
            This approach is what makes SyNeural more than a measurement
            platform — it makes it a framework for progression across the full
            spectrum of responsibility.
          </p>
        </motion.div>
      </section>
    </div>;
}